<template>
  <div
    class="pa-4 hyp-scenarios-card-wrapper"
    v-if="riskTypes && riskTypes.length"
  >
    <div
      v-for="(riskType, index) in riskTypes"
      :key="index"
      class="hyp-scenarios-card-wrapper"
    >
      <v-card
        flat
        class="pa-0 risk-impact-card"
        v-for="(impactSection, index) in riskType.impacts"
        :key="index"
      >
        <div class="pa-4">
          <h3
            v-if="impactSection.length"
            class="font-weight-bold text-capitalize"
          >
            {{ riskType.display }}
            <span v-if="index != 'outlook'">{{ index }}</span> Impacts
          </h3>
          <div v-for="(impact, index) in impactSection" :key="index">
            <v-container class="pa-0 my-2">
              <div class="hyp-scenario-impact">
                <div class="pa-0 ma-0 hyp-scenario-impact-icons-container">
                  <div
                    class="hyp-scenario-impact-icons mr-2"
                    :style="getImpactIconStyle(impact)"
                    v-html="getImpactDirectionIcon(impact.directionName)"
                  ></div>
                </div>
                <div class="pa-0">
                  <div class="text-capitalize font-weight-bold">
                    <span>{{ impact.riskCategoryName }}</span
                    >:&nbsp;<span>{{ impact.rateName }}</span>
                  </div>
                  <div
                    class="hyp-impact-text pa-0"
                    v-html="getImpactText(impact)"
                  ></div>
                </div>
              </div>
            </v-container>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashFind from "lodash/find";
import lodashFilter from "lodash/filter";
import lodashGet from "lodash/get";

import { removeHtmlTags } from "@/common/basic.utilities";
import { getRiskRatingCategories } from "@/services/controlRisks/risks.service";
import { RISK_LEVELS } from "@/common/constants/controlRisks/risks";
import { SCENARIOS_RISK_TYPE_IMPACTS_DIRECTION } from "@/common/constants/controlRisks/scenarios";

export default {
  name: "RisksScenariosRiskTypeImpacts",
  props: {
    title: {
      type: String,
    },
    scenarios: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      riskTypes: null,
    };
  },
  async mounted() {
    await this.getFormattedRiskTypeImpacts();
  },
  methods: {
    async getFormattedRiskTypeImpacts() {
      if (
        this.scenarios.outlook?.riskTypeImpacts?.length ||
        this.scenarios.alternative?.riskTypeImpacts?.length ||
        this.scenarios.outlier?.riskTypeImpacts?.length
      ) {
        const riskRatingCategories = await getRiskRatingCategories();

        this.riskTypes = lodashOrderBy(
          riskRatingCategories
            .filter((riskRatingCategory) => {
              return !riskRatingCategory.parentId;
            })
            .map((riskRatingCategory) => {
              riskRatingCategory.impacts = {};
              riskRatingCategory.impacts.outlook = lodashOrderBy(
                lodashFilter(
                  this.scenarios.outlook?.riskTypeImpacts,
                  (riskTypeImpact) => {
                    return (
                      riskTypeImpact.riskCategoryParentId ===
                      riskRatingCategory.id
                    );
                  }
                ),
                ["riskCategoryName"],
                ["asc"]
              );

              if (this.scenarios.alternative || this.scenarios.outlier) {
                riskRatingCategory.impacts.alternative = lodashOrderBy(
                  lodashFilter(
                    this.scenarios.alternative?.riskTypeImpacts,
                    (riskTypeImpact) => {
                      return (
                        riskTypeImpact.riskCategoryParentId ===
                        riskRatingCategory.id
                      );
                    }
                  ),
                  ["riskCategoryName"],
                  ["asc"]
                );

                riskRatingCategory.impacts.outlier = lodashOrderBy(
                  lodashFilter(
                    this.scenarios.outlier?.riskTypeImpacts,
                    (riskTypeImpact) => {
                      return (
                        riskTypeImpact.riskCategoryParentId ===
                        riskRatingCategory.id
                      );
                    }
                  ),
                  ["riskCategoryName"],
                  ["asc"]
                );
              }

              return riskRatingCategory;
            })
            .filter((riskRatingCategory) => {
              return (
                riskRatingCategory.impacts &&
                (riskRatingCategory.impacts.outlook?.length ||
                  riskRatingCategory.impacts.alternative?.length ||
                  riskRatingCategory.impacts.outlier?.length)
              );
            }),
          ["order"],
          ["asc"]
        );
      }
    },
    getImpactText(impact) {
      return removeHtmlTags(impact.text);
    },
    getImpactBgColor(value) {
      if (value) {
        const level = lodashFind(RISK_LEVELS, { value: value });
        if (lodashGet(level, "bgColor")) {
          return level.bgColor;
        }
      }
      return null;
    },
    getImpactStyle(impact) {
      if (impact && impact.rateValue) {
        const bgColor = this.getImpactBgColor(impact.rateValue);
        const colorRgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
          bgColor
        );
        return {
          border: `2px solid rgba(${parseInt(colorRgb[1], 16)}, ${parseInt(
            colorRgb[2],
            16
          )}, ${parseInt(colorRgb[3], 16)}, .6)`,
          "background-color": `rgba(${parseInt(colorRgb[1], 16)}, ${parseInt(
            colorRgb[2],
            16
          )}, ${parseInt(colorRgb[3], 16)}, .2)`,
        };
      }
      return null;
    },
    getImpactIconStyle(impact) {
      const bgColor = this.getImpactBgColor(impact.rateValue);
      return {
        "background-color": `${bgColor}`,
        fill: impact.rateValue > 4 ? "white" : "black",
      };
    },
    getImpactDirectionIcon(direction) {
      if (direction) {
        const directionConfig = lodashFind(
          SCENARIOS_RISK_TYPE_IMPACTS_DIRECTION,
          { name: direction }
        );
        if (lodashGet(directionConfig, "iconSvg")) {
          return directionConfig.iconSvg;
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.hyp-scenarios-card-wrapper {
  display: contents;
}
.hyp-scenario-impact {
  display: grid;
  grid-template-columns: 30px 1fr;
}
.hyp-scenario-impact-icons-container {
  margin: auto;
  padding-bottom: 0px;
  padding-top: 0px;
}
.hyp-scenario-impact-icons {
  border-radius: 28px;
  width: 22px;
  height: 22px;
  background-color: white;
  font-weight: bold;
  font-size: 13px;
  padding: 4px 5px;
}
.v-application .font-weight-bold {
  font-weight: 700 !important;
}
</style>
